// pages/MockTestResult.js
import React, { useState } from "react";
import { useLocation } from "react-router-dom";

// ── Print CSS injected into <head> ────────────────────────────
const PRINT_CSS = `
  @media print {
    .no-print { display: none !important; }
    .print-only { display: block !important; }
    body { background: #fff !important; }
    .vkr-page { background: #fff !important; padding: 0 !important; }
    .vkr-topbar { background: #1a237e !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .vkr-card { box-shadow: none !important; border: 1px solid #eee !important; page-break-inside: avoid; }
    .vkr-review-q { page-break-inside: avoid; }
    .vkr-score-big { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .vkr-stat-box  { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  @media screen {
    .print-only { display: none !important; }
  }
`;

// ── Helpers ───────────────────────────────────────────────────
function formatDuration(secs) {
  var m = Math.floor(secs / 60);
  var s = secs % 60;
  if (m === 0) return s + " sec";
  return m + " min " + (s > 0 ? s + " sec" : "");
}
function scoreColor(pct) {
  if (pct >= 75) return "#2e7d32";
  if (pct >= 50) return "#e65100";
  return "#c62828";
}
function scoreBg(pct) {
  if (pct >= 75) return "#e8f5e9";
  if (pct >= 50) return "#fff3e0";
  return "#ffebee";
}
function scoreEmoji(pct) {
  if (pct >= 90) return "🏆";
  if (pct >= 75) return "🎯";
  if (pct >= 50) return "👍";
  return "📚";
}

// ─────────────────────────────────────────────────────────────
export default function MockTestResult() {
  var location = useLocation();
  var state    = location.state;
  var [showReview, setShowReview] = useState(false);

  // ── Guard ─────────────────────────────────────────────────
  if (!state || !state.total) {
    return (
      <div style={{
        minHeight:"100vh", display:"flex", alignItems:"center",
        justifyContent:"center", fontFamily:"'Segoe UI',sans-serif",
        background:"#f0f4f8", textAlign:"center", padding:"24px",
      }}>
        <div style={{background:"#fff",padding:"32px",borderRadius:"14px",maxWidth:"400px",boxShadow:"0 2px 16px rgba(0,0,0,0.09)"}}>
          <div style={{fontSize:"36px",marginBottom:"12px"}}>🔗</div>
          <div style={{fontSize:"16px",fontWeight:"600",color:"#333",marginBottom:"8px"}}>No result data found</div>
          <div style={{fontSize:"13px",color:"#888",marginBottom:"20px"}}>
            Please complete a test first using the WhatsApp link your teacher sent you.
          </div>
          <button
            style={{padding:"10px 24px",background:"#1a237e",color:"#fff",border:"none",borderRadius:"8px",cursor:"pointer",fontWeight:"600"}}
            onClick={function() { window.location.href = "https://virtualkalvi.com"; }}
          >
            🏠 Go to VirtualKalvi
          </button>
        </div>
      </div>
    );
  }

  var {
    studentName   = "",
    correct       = 0,
    wrong         = 0,
    unattempted   = 0,
    total         = 0,
    scorePct      = 0,
    timeTakenSecs = 0,
    teacherWaLink = "",
    answersArr    = [],
    subject       = "",
    chapter       = "",
  } = state;

  // ── Subject-wise breakdown ─────────────────────────────────
  var subjectMap = {};
  answersArr.forEach(function(a) {
    var sub = a.subject || "General";
    if (!subjectMap[sub]) subjectMap[sub] = { correct: 0, wrong: 0, unattempted: 0 };
    if (!a.selected_option) subjectMap[sub].unattempted++;
    else if (a.is_correct)  subjectMap[sub].correct++;
    else                    subjectMap[sub].wrong++;
  });

  // ── Print / Save as PDF ────────────────────────────────────
  function handlePrint() {
    setShowReview(true);         // make sure review is visible before print
    setTimeout(function() {
      window.print();
    }, 300);                     // small delay so React re-renders review first
  }

  // ── Home ──────────────────────────────────────────────────
  function handleHome() {
    window.location.href = "https://virtualkalvi.com";
  }

  // ─────────────────────────────────────────────────────────
  return (
    <div className="vkr-page" style={{
      minHeight:"100vh", background:"#f0f4f8",
      fontFamily:"'Segoe UI', sans-serif", paddingBottom:"48px",
    }}>

      {/* ── Inject print CSS ── */}
      <style>{PRINT_CSS}</style>

      {/* ── Top bar ── */}
      <div className="vkr-topbar no-print" style={{
        background:"#1a237e", color:"#fff",
        padding:"14px 20px", fontSize:"17px",
        fontWeight:"700", textAlign:"center",
      }}>
        📊 VirtualKalvi — Test Result
      </div>

      {/* ── Print header (only shows when printing) ── */}
      <div className="print-only" style={{
        background:"#1a237e", color:"#fff",
        padding:"14px 20px", marginBottom:"16px",
        fontSize:"17px", fontWeight:"700", textAlign:"center",
      }}>
        📊 VirtualKalvi — Mock Test Result Report
      </div>

      {/* ── Main content ── */}
      <div style={{display:"flex", justifyContent:"center", flexDirection:"column", alignItems:"center", padding:"24px 16px 0 16px"}}>

        {/* ── Score Card ── */}
        <div className="vkr-card" style={{
          background:"#fff", borderRadius:"16px",
          boxShadow:"0 2px 16px rgba(0,0,0,0.09)",
          padding:"28px 24px", width:"100%", maxWidth:"560px", marginBottom:"16px",
        }}>

          {/* Big score */}
          <div className="vkr-score-big" style={{
            textAlign:"center", padding:"24px", borderRadius:"12px",
            background: scoreBg(scorePct), marginBottom:"20px",
          }}>
            <div style={{fontSize:"40px", marginBottom:"6px"}}>{scoreEmoji(scorePct)}</div>
            <div style={{fontSize:"42px", fontWeight:"800", color: scoreColor(scorePct)}}>
              {scorePct.toFixed(1)}%
            </div>
            <div style={{fontSize:"13px", color:"#777", marginTop:"4px"}}>
              {studentName} &nbsp;·&nbsp; {subject} {chapter ? "| " + chapter : ""}
            </div>
          </div>

          {/* Stats */}
          <div className="vkr-stat-row" style={{
            display:"grid", gridTemplateColumns:"1fr 1fr 1fr",
            gap:"10px", marginBottom:"20px",
          }}>
            <div className="vkr-stat-box" style={{textAlign:"center", padding:"14px 8px", borderRadius:"10px", background:"#e8f5e9"}}>
              <div style={{fontSize:"26px", fontWeight:"800", color:"#2e7d32"}}>{correct}</div>
              <div style={{fontSize:"11px", color:"#777", marginTop:"2px"}}>✅ Correct</div>
            </div>
            <div className="vkr-stat-box" style={{textAlign:"center", padding:"14px 8px", borderRadius:"10px", background:"#ffebee"}}>
              <div style={{fontSize:"26px", fontWeight:"800", color:"#c62828"}}>{wrong}</div>
              <div style={{fontSize:"11px", color:"#777", marginTop:"2px"}}>❌ Wrong</div>
            </div>
            <div className="vkr-stat-box" style={{textAlign:"center", padding:"14px 8px", borderRadius:"10px", background:"#f5f5f5"}}>
              <div style={{fontSize:"26px", fontWeight:"800", color:"#888"}}>{unattempted}</div>
              <div style={{fontSize:"11px", color:"#777", marginTop:"2px"}}>⬜ Skipped</div>
            </div>
          </div>

          {/* Summary */}
          <div style={{textAlign:"center", fontSize:"13px", color:"#888", marginBottom:"20px"}}>
            {correct} / {total} correct &nbsp;·&nbsp; Time: {formatDuration(timeTakenSecs)}
          </div>

          {/* Subject breakdown */}
          {Object.keys(subjectMap).length > 1 && (
            <div style={{marginBottom:"20px"}}>
              <div style={{fontSize:"13px", fontWeight:"700", color:"#555", marginBottom:"8px"}}>Subject-wise Breakdown</div>
              {Object.keys(subjectMap).map(function(sub) {
                var d = subjectMap[sub];
                var t2 = d.correct + d.wrong + d.unattempted;
                var p2 = t2 > 0 ? Math.round((d.correct / t2) * 100) : 0;
                return (
                  <div key={sub} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"10px 0", borderBottom:"1px solid #f0f0f0", fontSize:"14px",
                  }}>
                    <span style={{fontWeight:"600"}}>{sub}</span>
                    <span style={{color:"#888", fontSize:"13px"}}>
                      ✅ {d.correct} &nbsp; ❌ {d.wrong} &nbsp;
                      <span style={{fontWeight:"700", color: scoreColor(p2)}}>{p2}%</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Action Buttons ── */}
          <div className="no-print">

            {/* WhatsApp teacher */}
            {teacherWaLink && (
              <a href={teacherWaLink} target="_blank" rel="noreferrer" style={{
                display:"block", width:"100%", padding:"14px",
                background:"#25D366", color:"#fff", border:"none",
                borderRadius:"10px", fontSize:"15px", fontWeight:"700",
                cursor:"pointer", textAlign:"center", textDecoration:"none",
                boxSizing:"border-box", marginBottom:"10px",
              }}>
                📲 Send Result to Teacher on WhatsApp
              </a>
            )}

            {/* Download PDF */}
            <button onClick={handlePrint} style={{
              display:"block", width:"100%", padding:"12px",
              background:"#e53935", color:"#fff", border:"none",
              borderRadius:"10px", fontSize:"14px", fontWeight:"700",
              cursor:"pointer", textAlign:"center", marginBottom:"10px",
              boxSizing:"border-box",
            }}>
              📄 Download Result as PDF
            </button>

            {/* Review answers toggle */}
            <button onClick={function() { setShowReview(function(v) { return !v; }); }} style={{
              display:"block", width:"100%", padding:"12px",
              background:"#e8eaf6", color:"#3949ab", border:"none",
              borderRadius:"10px", fontSize:"14px", fontWeight:"600",
              cursor:"pointer", textAlign:"center", marginBottom:"10px",
              boxSizing:"border-box",
            }}>
              {showReview ? "▲ Hide Answer Review" : "▼ Review My Answers"}
            </button>

            {/* Home button */}
            <button onClick={handleHome} style={{
              display:"block", width:"100%", padding:"12px",
              background:"#f5f5f5", color:"#333", border:"1.5px solid #ddd",
              borderRadius:"10px", fontSize:"14px", fontWeight:"700",
              cursor:"pointer", textAlign:"center", boxSizing:"border-box",
            }}>
              🏠 Go to VirtualKalvi Home
            </button>

          </div>
        </div>

        {/* ── Answer Review Section ── */}
        {showReview && (
          <div className="vkr-card" style={{
            background:"#fff", borderRadius:"16px",
            boxShadow:"0 2px 16px rgba(0,0,0,0.09)",
            padding:"28px 24px", width:"100%", maxWidth:"560px", marginBottom:"16px",
          }}>
            <div style={{fontSize:"15px", fontWeight:"700", color:"#1a237e", marginBottom:"16px"}}>
              📋 Answer Review — {studentName}
            </div>

            {answersArr.map(function(a, i) {
              return (
                <div key={i} className="vkr-review-q" style={{
                  padding:"16px", borderRadius:"10px", marginBottom:"12px",
                  border:"1.5px solid #eee",
                  background: a.is_correct ? "#f9fbe7" : (!a.selected_option ? "#fafafa" : "#fff5f5"),
                }}>
                  {/* Question */}
                  <div style={{fontSize:"14px", fontWeight:"600", color:"#222", marginBottom:"10px"}}>
                    <span style={{color:"#888", marginRight:"8px"}}>Q{i + 1}.</span>
                    {a.question_text}
                    {a.subject && (
                      <span style={{fontSize:"11px", color:"#aaa", marginLeft:"8px", fontWeight:"400"}}>
                        [{a.subject}{a.chapter ? " · " + a.chapter : ""}]
                      </span>
                    )}
                  </div>

                  {/* Answer tags */}
                  <div>
                    {a.selected_option && (
                      <span style={{
                        display:"inline-block", padding:"4px 12px", borderRadius:"6px",
                        marginRight:"8px", fontSize:"13px", fontWeight:"600",
                        background: a.is_correct ? "#e8f5e9" : "#ffebee",
                        color:      a.is_correct ? "#2e7d32" : "#c62828",
                      }}>
                        {a.is_correct ? "✅" : "❌"} Your Answer: {a.selected_option}
                      </span>
                    )}

                    {!a.is_correct && (
                      <span style={{
                        display:"inline-block", padding:"4px 12px", borderRadius:"6px",
                        fontSize:"13px", fontWeight:"600",
                        background:"#e8f5e9", color:"#2e7d32",
                      }}>
                        ✅ Correct: {a.correct_option}
                      </span>
                    )}

                    {!a.selected_option && (
                      <span style={{fontSize:"12px", color:"#aaa"}}>
                        ⬜ Not attempted &nbsp;|&nbsp; Correct: {a.correct_option}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Print button inside review (visible on screen) */}
            <button className="no-print" onClick={handlePrint} style={{
              marginTop:"16px", width:"100%", padding:"12px",
              background:"#e53935", color:"#fff", border:"none",
              borderRadius:"10px", fontSize:"14px", fontWeight:"700",
              cursor:"pointer", boxSizing:"border-box",
            }}>
              📄 Download Full Report as PDF
            </button>

            {/* Home button inside review */}
            <button className="no-print" onClick={handleHome} style={{
              marginTop:"10px", width:"100%", padding:"12px",
              background:"#f5f5f5", color:"#333", border:"1.5px solid #ddd",
              borderRadius:"10px", fontSize:"14px", fontWeight:"700",
              cursor:"pointer", boxSizing:"border-box",
            }}>
              🏠 Go to VirtualKalvi Home
            </button>

          </div>
        )}

      </div>
    </div>
  );
}
